import { useEffect, useRef } from "react";

// Faithful port of the user's reference mountRadarButton (phase1_4/radar-button-reference.md), with full cleanup.
function mountRadarButton(btn) {
  const canvas = document.createElement("canvas");
  canvas.setAttribute("aria-hidden", "true");
  btn.insertBefore(canvas, btn.firstChild);
  const ctx = canvas.getContext("2d");
  const label = btn.querySelector(".rb-label");
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const GAP = 7,
    PERIOD = 3200;
  let W = 0,
    H = 0,
    dots = [],
    blips = [],
    t = 0,
    last = 0,
    speed = 1,
    target = 1,
    visible = true,
    raf = 0,
    alive = true;
  const TAU = Math.PI * 2;
  const nAngle = (x, y) => Math.atan2((y - H / 2) / H, (x - W / 2) / W);
  function inPill(x, y) {
    const r = H / 2,
      m = 2.5;
    if (x < r) return Math.hypot(x - r, y - r) <= r - m;
    if (x > W - r) return Math.hypot(x - (W - r), y - r) <= r - m;
    return y > m && y < H - m;
  }
  function layout() {
    const b = btn.getBoundingClientRect();
    W = b.width;
    H = b.height;
    if (!W || !H) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const l = label.getBoundingClientRect(),
      lx = l.left - b.left,
      ly = l.top - b.top;
    dots = [];
    for (let y = GAP / 2 + 0.5; y < H; y += GAP)
      for (let x = GAP / 2; x < W; x += GAP) {
        if (!inPill(x, y)) continue;
        const dx = Math.max(lx - 12 - x, 0, x - (lx + l.width + 12)),
          dy = Math.max(ly - 3 - y, 0, y - (ly + l.height + 3));
        const keep = Math.min(1, Math.hypot(dx, dy) / 16);
        dots.push({ x, y, a: nAngle(x, y), k: 0.08 + 0.92 * keep, j: Math.random() * TAU });
      }
    pickBlips();
    draw();
  }
  function pickBlips() {
    const pool = dots.filter((d) => d.k > 0.95 && d.x > H * 0.6 && d.x < W - H * 0.6 && d.y > 10 && d.y < H - 10);
    blips = [];
    while (blips.length < 3 && pool.length) {
      const d = pool.splice(Math.floor(Math.random() * pool.length), 1)[0];
      if (blips.every((b) => Math.hypot(b.d.x - d.x, b.d.y - d.y) >= 24)) blips.push({ d, life: 0, hit: false });
    }
  }
  function pill(i) {
    const r = Math.max(0, (H - 2 * i) / 2);  // canvas can be 0 px high while hidden (mobile placer): never a negative radius
    ctx.beginPath();
    ctx.moveTo(i + r, i);
    ctx.lineTo(W - i - r, i);
    ctx.arc(W - i - r, H / 2, r, -Math.PI / 2, Math.PI / 2);
    ctx.lineTo(i + r, H - i);
    ctx.arc(i + r, H / 2, r, Math.PI / 2, Math.PI * 1.5);
    ctx.closePath();
  }
  function draw() {
    ctx.clearRect(0, 0, W, H);
    const s = (t / PERIOD) * TAU,
      cs = Math.cos(s),
      sn = Math.sin(s);
    if (!reduce) {
      const px = W / 2 + cs * W * 0.4,
        py = H / 2 + sn * H * 0.4,
        g = ctx.createRadialGradient(px, py, 0, px, py, H * 1.8);
      g.addColorStop(0, "rgba(255,255,255,0.09)");
      g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.save();
      pill(0);
      ctx.clip();
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, W, H);
      ctx.restore();
    }
    for (let i = 0; i < dots.length; i++) {
      const d = dots[i],
        behind = (((s - d.a) % TAU) + TAU) % TAU,
        glow = behind < 2.2 ? Math.pow(1 - behind / 2.2, 2) : 0;
      const a = reduce ? 0.16 * d.k : (0.09 + 0.05 * Math.sin(t / 700 + d.j) + 0.78 * glow) * d.k;
      if (a < 0.02) continue;
      ctx.fillStyle = "rgba(255,255,255," + a.toFixed(3) + ")";
      ctx.beginPath();
      ctx.arc(d.x, d.y, 0.85, 0, TAU);
      ctx.fill();
    }
    if (!reduce)
      for (let k = 0; k < blips.length; k++) {
        const bl = blips[k],
          bh = (((s - bl.d.a) % TAU) + TAU) % TAU;
        if (bh < 0.12 && !bl.hit) {
          bl.life = 1;
          bl.hit = true;
        }
        if (bh > 3) bl.hit = false;
        if (bl.life > 0) {
          ctx.fillStyle = "rgba(255,255,255," + (0.95 * bl.life).toFixed(3) + ")";
          ctx.beginPath();
          ctx.arc(bl.d.x, bl.d.y, 1.5, 0, TAU);
          ctx.fill();
          ctx.strokeStyle = "rgba(255,255,255," + (0.45 * bl.life).toFixed(3) + ")";
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(bl.d.x, bl.d.y, 1.5 + 7 * (1 - bl.life), 0, TAU);
          ctx.stroke();
        }
      }
    ctx.lineWidth = 1;
    pill(0.5);
    ctx.strokeStyle = "rgba(255,255,255,0.13)";
    ctx.stroke();
    if (!reduce) {
      const bx = W / 2 + cs * W * 0.5,
        by = H / 2 + sn * H * 0.5,
        g2 = ctx.createRadialGradient(bx, by, 0, bx, by, W * 0.38);
      g2.addColorStop(0, "rgba(255,255,255,0.32)");
      g2.addColorStop(0.45, "rgba(255,255,255,0.10)");
      g2.addColorStop(1, "rgba(255,255,255,0)");
      pill(0.5);
      ctx.strokeStyle = g2;
      ctx.stroke();
    }
  }
  function frame(now) {
    const dt = last ? Math.min(64, now - last) : 16;
    last = now;
    speed += (target - speed) * 0.08;
    t += dt * speed;
    for (let k = 0; k < blips.length; k++) if (blips[k].life > 0) blips[k].life = Math.max(0, blips[k].life - (dt * speed) / (PERIOD * 0.85));
    if (Math.floor((t - dt * speed) / (PERIOD * 3)) !== Math.floor(t / (PERIOD * 3))) pickBlips();
    draw();
    raf = alive && visible && !document.hidden ? requestAnimationFrame(frame) : 0;
  }
  function start() {
    if (alive && !raf && !reduce) {
      last = 0;
      raf = requestAnimationFrame(frame);
    }
  }
  const fast = () => (target = 1.7);
  const slow = () => (target = 1);
  const press = () => (target = 2.2);
  const onVis = () => !document.hidden && start();
  btn.addEventListener("pointerenter", fast);
  btn.addEventListener("focus", fast);
  btn.addEventListener("pointerleave", slow);
  btn.addEventListener("blur", slow);
  btn.addEventListener("pointerdown", press);
  const ro = new ResizeObserver(layout);
  ro.observe(btn);
  const io = new IntersectionObserver((es) => {
    visible = es[0].isIntersecting;
    if (visible) start();
  });
  io.observe(btn);
  document.addEventListener("visibilitychange", onVis);
  (document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve()).then(() => {
    if (!alive) return;
    layout();
    start();
  });
  return () => {
    alive = false;
    if (raf) cancelAnimationFrame(raf);
    ro.disconnect();
    io.disconnect();
    document.removeEventListener("visibilitychange", onVis);
    btn.removeEventListener("pointerenter", fast);
    btn.removeEventListener("focus", fast);
    btn.removeEventListener("pointerleave", slow);
    btn.removeEventListener("blur", slow);
    btn.removeEventListener("pointerdown", press);
    canvas.remove();
  };
}

export default function RadarButton({ children, className = "", type = "button", ...props }) {
  const ref = useRef(null);
  useEffect(() => mountRadarButton(ref.current), []);
  return (
    <button ref={ref} type={type} className={`radar-btn ${className}`} {...props}>
      <span className="rb-label">{children}</span>
    </button>
  );
}
